/**
 * Others' selections in a PDF's text (textPlaces.ts), tinted in their colour as in a live document (the same look:
 * office.css, .xcaret-sel), each line one box; over the window (as a document's carets are), clipped to the viewer.
 * While their pointer's over it, a selection shows as late as their pointer does (POINTER.selectionDelayMs): the two
 * move together.
 */

import { useEffect, useRef, useState } from "preact/hooks";
import { lines } from "../../office/sync/lines.ts";
import { POINTER } from "../../office/sync/pointers.ts";
import { selectionRects, type TextSelection } from "./textPlaces.ts";

/** Someone selecting: their entry, colour, selection (null: none) and whether their pointer's over the PDF. */
export interface Selecting {
  entry: string;
  colour: string;
  sel: TextSelection | null;
  pointing: boolean;
}

interface Held {
  got: string;
  shown: TextSelection | null;
  next: TextSelection | null;
  due: number;
}

/** Their selections over the PDF in `scroller`. */
export function PdfSelections({
  scroller,
  people,
  zoom,
}: {
  scroller: { current: HTMLElement | null };
  people: readonly Selecting[];
  /** The zoom the pages are at now (their text may be laid out at another while it changes: textPlaces.ts). */
  zoom: number;
}) {
  const held = useRef(new Map<string, Held & { colour: string }>());
  const [tick, setTick] = useState(0);
  const now = performance.now();
  const map = held.current;
  for (const p of people) {
    const got = JSON.stringify(p.sel);
    const h = map.get(p.entry);
    if (!h) {
      map.set(p.entry, { got, shown: p.sel, next: null, due: 0, colour: p.colour });
    } else if (h.got !== got) {
      h.got = got;
      if (p.pointing) {
        h.next = p.sel;
        h.due = now + POINTER.selectionDelayMs;
      } else {
        h.shown = p.sel;
        h.next = null;
      }
    }
    if (h) {
      h.colour = p.colour;
    }
  }
  for (const entry of map.keys()) {
    if (!people.some((p) => p.entry === entry)) {
      map.delete(entry);
    }
  }
  // A selection held back: shown when it's due.
  let due = Number.POSITIVE_INFINITY;
  for (const h of map.values()) {
    if (h.next !== null || h.due > 0) {
      if (h.due <= now) {
        h.shown = h.next;
        h.next = null;
        h.due = 0;
      } else {
        due = Math.min(due, h.due);
      }
    }
  }
  useEffect(() => {
    if (due === Number.POSITIVE_INFINITY) {
      return;
    }
    const t = setTimeout(() => setTick((n) => n + 1), due - performance.now() + 1);
    return () => clearTimeout(t);
  });
  const [layer] = useState(() => {
    const el = document.createElement("div");
    el.className = "xdoc-carets";
    el.setAttribute("aria-hidden", "true");
    return el;
  });
  const draw = useRef<() => void>(() => undefined);
  draw.current = () => {
    const root = scroller.current;
    const view = root?.getBoundingClientRect();
    if (!(root && view)) {
      layer.replaceChildren();
      return;
    }
    layer.style.clipPath = `inset(${view.top}px ${innerWidth - view.right}px ${innerHeight - view.bottom}px ${view.left}px)`;
    const boxes: HTMLElement[] = [];
    for (const h of map.values()) {
      if (!h.shown) {
        continue;
      }
      for (const r of lines(selectionRects(root, h.shown, zoom))) {
        const b = document.createElement("div");
        b.className = "xcaret-sel";
        b.style.cssText = `left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px`;
        b.style.setProperty("--xcaret", h.colour);
        boxes.push(b);
      }
    }
    layer.replaceChildren(...boxes);
  };
  // Drawn again as it scrolls or zooms, and as pages' text is drawn (a selection reaching into them).
  useEffect(() => {
    const root = scroller.current;
    if (!root) {
      return;
    }
    document.body.append(layer);
    let frame = 0;
    const again = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => draw.current());
    };
    const seen = new MutationObserver(again);
    seen.observe(root, { childList: true, subtree: true });
    root.addEventListener("scroll", again, { passive: true });
    globalThis.addEventListener("resize", again);
    return () => {
      cancelAnimationFrame(frame);
      seen.disconnect();
      root.removeEventListener("scroll", again);
      globalThis.removeEventListener("resize", again);
      layer.remove();
    };
  }, [scroller, layer]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `people`, `tick` and `zoom` are the triggers: theirs changed, one came due, or the pages grew or shrank.
  useEffect(() => draw.current(), [people, tick, zoom]);
  return null;
}
